import type { NoteColor, NoteScope, NoteType, RichDoc } from "@sebas/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Profile } from "./api";

export type NoteSummary = {
  id: string;
  title: string;
  preview: string;
  color: NoteColor;
  noteType: NoteType;
  icon: string | null;
  isPinned: boolean;
  isArchived: boolean;
  teamId: string | null;
  ownerId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type Note = NoteSummary & {
  content: RichDoc;
  contentText: string;
  templateData: Record<string, unknown>;
};
export type Tag = { id: string; name: string; color: NoteColor; teamId: string | null };

export type NoteFilters = {
  q?: string;
  /** Which library the list is showing. Omitted means everything reachable. */
  scope?: NoteScope;
  teamId?: string;
  tagId?: string;
  noteType?: NoteType;
  archived?: boolean;
  pinned?: boolean;
};

function toQuery(f: NoteFilters): string {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.scope) p.set("scope", f.scope);
  if (f.teamId) p.set("teamId", f.teamId);
  if (f.tagId) p.set("tagId", f.tagId);
  if (f.noteType) p.set("noteType", f.noteType);
  if (f.archived !== undefined) p.set("archived", String(f.archived));
  if (f.pinned !== undefined) p.set("pinned", String(f.pinned));
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const noteKeys = {
  all: ["notes"] as const,
  list: (f: NoteFilters) => ["notes", "list", f] as const,
  detail: (id: string) => ["notes", "detail", id] as const,
};

export function useNotes(filters: NoteFilters = {}) {
  return useQuery({
    queryKey: noteKeys.list(filters),
    queryFn: () => api<{ notes: NoteSummary[]; nextCursor: string | null }>(`/v1/notes${toQuery(filters)}`),
    // Search results change as you type; a short stale window keeps it snappy
    // without hammering the API on every keystroke.
    staleTime: 5_000,
  });
}

export function useNote(id: string | undefined) {
  return useQuery({
    queryKey: noteKeys.detail(id ?? ""),
    queryFn: () => api<{ note: Note; tags: Tag[]; access: string }>(`/v1/notes/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      input: Partial<Pick<Note, "title" | "color" | "teamId" | "noteType">> & {
        content?: RichDoc;
        templateData?: Record<string, unknown>;
      },
    ) =>
      api<{ note: Note }>("/v1/notes", { method: "POST", body: JSON.stringify(input) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: noteKeys.all }),
  });
}

export type UpdateNotePayload = {
  id: string;
  title?: string;
  content?: RichDoc;
  color?: NoteColor;
  isPinned?: boolean;
  isArchived?: boolean;
  /** Required whenever `content` is present — the server rejects otherwise. */
  version?: number;
};

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateNotePayload) =>
      api<{ note: Note }>(`/v1/notes/${id}`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: (res) => {
      // Seed the detail cache with the server's row so `version` stays current;
      // a stale version here is what produces spurious 409s on the next save.
      qc.setQueryData(noteKeys.detail(res.note.id), (old: unknown) =>
        old && typeof old === "object" ? { ...(old as object), note: res.note } : old,
      );
      qc.invalidateQueries({ queryKey: ["notes", "list"] });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/notes/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: noteKeys.all }),
  });
}

export function useTags() {
  return useQuery({ queryKey: ["tags"], queryFn: () => api<{ tags: Tag[] }>("/v1/tags") });
}

/**
 * The signed-in user's profile.
 *
 * Shared by the shell's avatar and the Settings screen so the two never disagree
 * and `/v1/me` is fetched once. The row only changes when the user edits it, so
 * the window is long — the app chrome should not refetch it on every focus.
 */
export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: () => api<{ profile: Profile }>("/v1/me"),
    select: (r) => r.profile,
    staleTime: 5 * 60_000,
  });
}
